import { useEffect } from 'react';
import { startRingtone, stopRingtone } from '../lib/audio';
import type { CallsApi } from '../hooks/useCalls';
import { Avatar } from './Avatar';

/**
 * Estado da chamada na barra superior.
 *
 * Era uma sobreposição no meio da tela, com fundo escurecido: enquanto o telefone
 * tocava ou a ligação corria, o atendente não conseguia ler a conversa nem
 * consultar a base — exatamente o que ele precisa fazer **enquanto** fala com o
 * cliente. Aqui o estado fica visível o tempo todo e o resto do painel continua
 * utilizável; para chamar atenção sobram o toque, a pulsação e a cor.
 */
export function CallBar({ calls }: { calls: CallsApi }) {
  const { state } = calls;
  useRingtone(state.phase === 'incoming');

  if (state.phase === 'idle') return null;

  const who = state.peerName ?? formatPhone(state.peerPhone);

  return (
    <div
      className={`callbar callbar--${state.phase}`}
      role="status"
      aria-live="assertive"
      aria-label="Chamada"
    >
      <span className="callbar__dot" aria-hidden="true" />

      {/* Quem está ligando, com a foto do WhatsApp dele: é aqui que reconhecer o
          cliente vale mais, porque o atendente tem segundos para decidir se
          atende — e um número solto não diz nada a ninguém. */}
      {state.routing?.contactId && (
        <Avatar
          kind="contact"
          id={state.routing.contactId}
          name={state.peerName}
          version={state.routing.contactAvatarUpdatedAt}
          size="sm"
        />
      )}

      <span className="callbar__text">
        <strong>{labelFor(state.phase, state.direction)}</strong>
        <span className="callbar__who" title={formatPhone(state.peerPhone)}>
          {who}
        </span>
      </span>

      {state.phase === 'active' && (
        <span className="callbar__timer">{formatSeconds(state.seconds)}</span>
      )}

      {state.error && <span className="callbar__error">{state.error}</span>}

      <span className="callbar__actions">
        {state.phase === 'incoming' && (
          <>
            <button className="call-btn call-btn--accept" onClick={() => void calls.accept()}>
              Atender
            </button>
            <button className="call-btn call-btn--reject" onClick={() => void calls.reject()}>
              Recusar
            </button>
          </>
        )}

        {state.phase === 'dialing' && (
          <button className="call-btn call-btn--reject" onClick={() => void calls.hangup()}>
            Cancelar
          </button>
        )}

        {state.phase === 'active' && (
          <>
            <button
              className={`call-btn${state.muted ? ' call-btn--on' : ''}`}
              onClick={() => void calls.toggleMute()}
            >
              {state.muted ? 'Reativar' : 'Mudo'}
            </button>
            <button className="call-btn call-btn--reject" onClick={() => void calls.hangup()}>
              Desligar
            </button>
          </>
        )}

        {state.phase === 'ended' && (
          <button className="call-btn" onClick={calls.dismiss}>
            Fechar
          </button>
        )}
      </span>
    </div>
  );
}

/**
 * Liga o toque enquanto a chamada está entrando. O oscilador vive em lib/audio
 * porque o AudioContext é compartilhado com o aviso de mensagem — e é ele que
 * já foi desbloqueado no primeiro gesto do usuário.
 */
function useRingtone(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    startRingtone();
    return () => stopRingtone();
  }, [active]);
}

function labelFor(phase: string, direction: string | null): string {
  if (phase === 'incoming') return 'Chamada recebida';
  if (phase === 'dialing') return 'Chamando';
  if (phase === 'active') return direction === 'inbound' ? 'Em atendimento' : 'Em chamada';
  return 'Chamada encerrada';
}

function formatSeconds(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${String(minutes).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
}

/** `5511999998888` → `+55 (11) 99999-8888` */
export function formatPhone(phone: string | null): string {
  if (!phone) return 'Número desconhecido';
  const digits = phone.replace(/\D/g, '');
  const match = /^(\d{2})(\d{2})(\d{4,5})(\d{4})$/.exec(digits);
  return match ? `+${match[1]} (${match[2]}) ${match[3]}-${match[4]}` : `+${digits}`;
}
